import { FunctionComponent, useEffect, useMemo, useState } from "react"
import { WellNotesFilterI, WellNotesSortConfigI } from "./list"
import { useNavigate } from "react-router-dom"
import { debounce } from "lodash"
import dayjs from "../../../../util/dayjs"
import styles from "./header.module.scss"
import { WELL_NOTES_ABS_ROUTE, WELL_NOTES_ADD_CHILD_ROUTE } from "../../../.."
import Button from "../../../shared/button"
import Input from "../../../shared/input"
import { DateRangePicker } from "rsuite"
import ReactMultiSelect, {
    ReactMultiSelectOptionT,
} from "../../../shared/input/react-select/multiSelect"
import {
    faChevronDoubleDown,
    faChevronDoubleUp,
    faChevronDown,
    faChevronUp,
    faCircleInfo,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useBreakPointContext } from "../../../../context/breakPointContext"
import { sortArrayByKey } from "../../../../util/arrays/array"
import ToolTip from "../../../shared/toolTip/tooltip"

interface WellNotesHeader {
    wellId: string
    searchTerm: string
    handleSearchTermChange: (value: string) => void
    sortConfig: WellNotesSortConfigI
    handleSortConfigChange: (config: WellNotesSortConfigI) => void
    filters: WellNotesFilterI
    onFilterChange: (filters: WellNotesFilterI) => void
    uniqueUsers: { id: string; name: string }[]
    isReadingWidth: boolean
    onToggleReadingWidth: () => void
    profileTimezoneLabel: string
    profileTimezoneIana: string
    resetAll: () => void
}

const DATE_FILTER_FORMAT = "YYYY-MM-DD"

// Convert a local Date from the picker into a date-only string.
const toYmdFromLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
}

// Create a local Date that preserves only the calendar date.
const toLocalDateFromYmd = (ymd: string): Date => {
    const [yearString, monthString, dayString] = ymd.split("-")
    const year = Number(yearString)
    const month = Number(monthString)
    const day = Number(dayString)
    return new Date(year, month - 1, day, 12, 0, 0, 0)
}

const WellNotesListHeader: FunctionComponent<WellNotesHeader> = ({
    wellId,
    searchTerm,
    sortConfig,
    filters,
    uniqueUsers,
    handleSearchTermChange,
    handleSortConfigChange,
    onFilterChange,
    isReadingWidth,
    onToggleReadingWidth,
    profileTimezoneLabel,
    profileTimezoneIana,
    resetAll,
}) => {
    // hooks
    const navigate = useNavigate()
    const breakPoint = useBreakPointContext()

    // state
    const [inputValue, setInputValue] = useState(searchTerm)
    const [showActions, setShowActions] = useState(true)
    const dateRangeShortcuts = useMemo(
        () => [
            {
                label: "Today",
                value: [
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .format(DATE_FILTER_FORMAT)
                    ),
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .format(DATE_FILTER_FORMAT)
                    ),
                ] as [Date, Date],
                placement: "bottom" as const,
            },
            {
                label: "Yesterday",
                value: [
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .subtract(1, "day")
                            .format(DATE_FILTER_FORMAT)
                    ),
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .subtract(1, "day")
                            .format(DATE_FILTER_FORMAT)
                    ),
                ] as [Date, Date],
                placement: "bottom" as const,
            },
            {
                label: "Last 7 Days",
                value: [
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .subtract(6, "day")
                            .format(DATE_FILTER_FORMAT)
                    ),
                    toLocalDateFromYmd(
                        dayjs()
                            .tz(profileTimezoneIana)
                            .format(DATE_FILTER_FORMAT)
                    ),
                ] as [Date, Date],
                placement: "bottom" as const,
            },
            {
                label: <span className={styles.clearShortcutLabel}>Clear</span>,
                value: null,
                placement: "bottom" as const,
            },
        ],
        [profileTimezoneIana]
    )
    const dateRangeValue = useMemo<[Date, Date] | null>(() => {
        const startDate = filters.dateRange?.startDate
        const endDate = filters.dateRange?.endDate

        if (!startDate || !endDate) {
            return null
        }

        return [toLocalDateFromYmd(startDate), toLocalDateFromYmd(endDate)]
    }, [filters.dateRange?.endDate, filters.dateRange?.startDate])

    const [createdByOptions, setCreatedByOptions] = useState<
        ReactMultiSelectOptionT[]
    >(
        uniqueUsers.map((user) => ({
            value: user.id,
            label: user.name,
            isSelected: filters.createdBy.includes(user.id),
            isDisabled: false,
        }))
    )

    // handlers
    const handleSelectClose = () => {
        const sortOpts = sortArrayByKey(
            createdByOptions,
            (opt) => (opt.isSelected ? "1" : "0"),
            false,
            "desc"
        )
        setCreatedByOptions(sortOpts)
    }

    // debounce on search change
    const debouncedSearchChange = useMemo(() => {
        return debounce((value: string) => handleSearchTermChange(value), 150)
    }, [handleSearchTermChange])

    useEffect(() => {
        return () => {
            // Cleanup when function changes
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    /** constants */
    // determine input orientation based on small breakpoint
    // src/styles/_breakpoint.scss  && src/context/breakPointContext/index.tsx
    const isSmallBreakPoint = breakPoint === "sm"
    const inputOrientation = "horizontal"

    // font awesome icon
    const fontAwesomeIcon = showActions
        ? faChevronDoubleUp
        : faChevronDoubleDown

    const outerClass = isSmallBreakPoint
        ? styles.outer_small
        : styles.outer_gt_small

    // on the event of an additional added note that adds a new created by user we need to update the options to include that user.
    useEffect(() => {
        setCreatedByOptions(
            uniqueUsers.map((user) => ({
                value: user.id,
                label: user.name,
                isSelected: filters.createdBy.includes(user.id),
                isDisabled: false,
            }))
        )
    }, [uniqueUsers])

    // if we resize from small => desktop sizing we need to setShowActions to true
    // if we resize from desktop => small, show actions will be true b/c users cannot collapse actions in desktop
    useEffect(() => {
        if (!isSmallBreakPoint) {
            setShowActions(true)
        }
    }, [isSmallBreakPoint])

    return (
        <div className={outerClass}>
            {isSmallBreakPoint && (
                <Button
                    handleButtonClick={() => setShowActions(!showActions)}
                    status="primary"
                    customButtonClassName={styles.mobileDropDownContainer}
                >
                    <FontAwesomeIcon icon={fontAwesomeIcon} color="#fff" />
                </Button>
            )}
            {showActions && (
                <div>
                    <div className={styles.listControlContainer}>
                        <div className={styles.flexLeft}>
                            <div className={styles.inputContainer}>
                                <Button
                                    status="primary"
                                    condition="default"
                                    handleButtonClick={() => {
                                        navigate(
                                            `${WELL_NOTES_ABS_ROUTE(
                                                wellId
                                            )}/${WELL_NOTES_ADD_CHILD_ROUTE}`
                                        )
                                    }}
                                >
                                    Add Well Note
                                </Button>
                            </div>
                            <div className={styles.inputContainer}>
                                <DateRangePicker
                                    value={dateRangeValue}
                                    format="MMM d, yyyy"
                                    character=" - "
                                    size="sm"
                                    editable
                                    cleanable
                                    placement="bottomStart"
                                    placeholder="Filter Date Range"
                                    className={styles.dateRangePicker}
                                    ranges={dateRangeShortcuts}
                                    onChange={(nextValue) => {
                                        if (!nextValue) {
                                            onFilterChange({
                                                ...filters,
                                                dateRange: {
                                                    startDate: "",
                                                    endDate: "",
                                                },
                                            })
                                            return
                                        }

                                        const [startDate, endDate] = nextValue
                                        onFilterChange({
                                            ...filters,
                                            dateRange: {
                                                startDate:
                                                    toYmdFromLocalDate(
                                                        startDate
                                                    ),
                                                endDate:
                                                    toYmdFromLocalDate(endDate),
                                            },
                                        })
                                    }}
                                />
                            </div>
                            <div className={styles.multiSelectContainer}>
                                <ReactMultiSelect
                                    options={createdByOptions}
                                    onChange={(newValue) => {
                                        // update the filters for the list component to use
                                        const selectedUserIds = newValue.map(
                                            (option: ReactMultiSelectOptionT) =>
                                                option.value
                                        )
                                        onFilterChange({
                                            ...filters,
                                            createdBy: selectedUserIds,
                                        })

                                        // update the options for select
                                        const updatedOpts =
                                            createdByOptions.map((option) => ({
                                                ...option,
                                                isSelected:
                                                    selectedUserIds.includes(
                                                        option.value
                                                    ),
                                            }))
                                        setCreatedByOptions(updatedOpts)
                                    }}
                                    onMenuClose={handleSelectClose}
                                    width="220px"
                                    orientation={inputOrientation}
                                    placeholder="Filter Created By"
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            width: "100%",
                                            height: 32,
                                            minHeight: 32,
                                            fontSize: 13,
                                        }),
                                    }}
                                />
                            </div>
                            {!isSmallBreakPoint && (
                                <div className={styles.widthToggleGroup}>
                                    <button
                                        type="button"
                                        className={`${
                                            styles.widthToggleOption
                                        } ${
                                            !isReadingWidth
                                                ? styles.widthToggleOptionActive
                                                : ""
                                        }`}
                                        aria-label="Use full width"
                                        onClick={() => {
                                            if (isReadingWidth) {
                                                onToggleReadingWidth()
                                            }
                                        }}
                                    >
                                        Full
                                    </button>
                                    <button
                                        type="button"
                                        className={`${
                                            styles.widthToggleOption
                                        } ${
                                            isReadingWidth
                                                ? styles.widthToggleOptionActive
                                                : ""
                                        }`}
                                        aria-label="Use compact width"
                                        onClick={() => {
                                            if (!isReadingWidth) {
                                                onToggleReadingWidth()
                                            }
                                        }}
                                    >
                                        Compact
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={styles.flexRight}>
                            <div className={styles.inputContainer}>
                                <Input
                                    type="text"
                                    placeHolder="Search Notes"
                                    orientation={inputOrientation}
                                    value={inputValue}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setInputValue(value)
                                        debouncedSearchChange(value)
                                    }}
                                    specializedClass="noMessage"
                                    inputCustomClass={styles.searchInput}
                                />
                            </div>
                            <div
                                onClick={() =>
                                    handleSortConfigChange({
                                        field: "occurredAt",
                                        direction:
                                            sortConfig.direction === "desc"
                                                ? "asc"
                                                : "desc",
                                    })
                                }
                                className={styles.sortContainer}
                            >
                                <label>Sort Date:</label>
                                <FontAwesomeIcon
                                    className={styles.faiSort}
                                    icon={
                                        sortConfig.direction === "desc"
                                            ? faChevronDown
                                            : faChevronUp
                                    }
                                />
                            </div>
                            <ToolTip
                                side="bottom"
                                tooltipContent={
                                    <div className={styles.sortTooltipContent}>
                                        <div>
                                            Times shown in your profile
                                            timezone: {profileTimezoneLabel}
                                        </div>
                                        <div>
                                            Sort Date orders notes by occurred
                                            at (interval start, when
                                            applicable).
                                        </div>
                                    </div>
                                }
                                triggerChildren={
                                    <button
                                        type="button"
                                        className={styles.sortInfoTrigger}
                                        aria-label="Sort and timezone info"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCircleInfo}
                                            className={styles.sortInfoIcon}
                                        />
                                    </button>
                                }
                            />
                            <div className={styles.clearAll}>
                                <Button
                                    status="secondary"
                                    condition="default"
                                    handleButtonClick={() => {
                                        setInputValue("")
                                        resetAll()
                                    }}
                                >
                                    Reset Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default WellNotesListHeader
